import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useAuth from '../../../../features/hooks/useAuth';
import { useLazyGetAssetsFinalFilteredQuery } from '../../../../features/addassets/addassetfinalApiSlice';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';


// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  cursor: pointer;
  color: #1976d2;
  &:hover {
    text-decoration: underline;
  }
`;

// New styled components for the Basic Details section
const BasicDetailsCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
  padding: 20px;
  border-top: 4px solid #1976d2;
`;

const DetailsTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const DetailItem = styled.div`
  margin-bottom: 10px;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  color: #666;
  margin-bottom: 5px;
  font-size: 14px;
`;

const DetailValue = styled.div`
  color: #333;
  font-weight: 500;
`;

// Renamed to avoid conflicts
const TimelineCard = styled.div`
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 20px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background-color: ${props => {
    if (props.status === 'rejected') return '#fff8f8';
    if (props.status === 'accepted') return '#f1f8e9';
    return props.isEven ? '#f8f9fa' : '#fff';
  }};
  border-left: 3px solid ${props => {
    if (props.status === 'pending') return '#f57c00';
    if (props.status === 'rejected') return '#d32f2f';
    if (props.status === 'accepted') return '#4caf50';
    return '#388e3c';
  }};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.1);
  }
`;

// Updated Timeline components for more minimalistic design
const TimelineContainer = styled.div`
  margin-top: 24px;
  padding-left: 16px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2px;
    background-color: #e0e0e0;
    z-index: 0;
  }
`;

const TimelineItemContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 24px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #1976d2;
    z-index: 1;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const TimelineTitle = styled.div`
  font-size: 17px;
  font-weight: 500;
  color: #202124;
  letter-spacing: -0.01em;
`;

const TimelineTime = styled.div`
  font-size: 14px;
  color: #5f6368;
`;

const AssetDetails = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
`;

const AssetTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  th {
    background-color: #e3f2fd;
    font-weight: 600;
  }
  
  tr:hover {
    background-color: #f5f5f5;
  }
`;

const Remark = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  
  h4 {
    margin: 0 0 10px 0;
    color: #333;
  }
`;

const UserInfo = styled.div`
  margin-top: 15px;
  padding: 10px;
  background: #e3f2fd;
  border-radius: 4px;
  font-size: 14px;
`;

const getStatusColor = (status) => {
  if (status === 'accepted') return '#e8f5e9';
  if (status === 'pending') return '#fff8f1';
  if (status === 'rejected') return '#ffebee';
  return '#e0e0e0';
};

const StatusBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background: ${props => {
    if (props.status === 'accepted' || props.status === 'closed') return '#e8f5e9';
    if (props.status === 'pending') return '#fff8f1';
    if (props.status === 'rejected') return '#ffebee';
    return '#f5f5f5';
  }};
  color: ${props => {
    if (props.status === 'accepted' || props.status === 'closed') return '#2e7d32';
    if (props.status === 'pending') return '#ed6c02';
    if (props.status === 'rejected') return '#d32f2f';
    return '#757575';
  }};
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 18px;
  color: #666;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 18px;
  color: #d32f2f;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 18px;
  color: #666;
`;

const PartialAssetsSection = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: ${props => props.status === 'accepted' ? '#e8f5e9' : '#fff8f1'};
  border-radius: 8px;
  border: 1px solid ${props => props.status === 'accepted' ? '#c8e6c9' : '#ffe0b2'};
`;

const PartialAssetsTitle = styled.h4`
  margin: 0 0 15px 0;
  color: ${props => props.status === 'accepted' ? '#2e7d32' : '#e65100'};
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AssetSectionHeader = styled.div`
  background-color: ${props => 
    props.status === 'accepted' ? '#e8f5e9' : 
    props.status === 'pending' ? '#fff8f1' : 
    props.status === 'rejected' ? '#ffebee' :
    '#ffffff'
  };
  padding: 12px;
  border-radius: 4px 4px 0 0;
  margin-top: 16px;
`;

const AssetRow = styled.tr`
  &:nth-child(odd) {
    background-color: ${props => {
      if (props.status === 'accepted') return '#f1f8e9';
      if (props.status === 'rejected') return '#fff8f8';
      if (props.status === 'pending') return '#fff8f1';
      return '#f8f9fa';
    }};
  }
  
  &:nth-child(even) {
    background-color: ${props => {
      if (props.status === 'accepted') return '#e8f5e9';
      if (props.status === 'rejected') return '#ffebee';
      if (props.status === 'pending') return '#fff3e0';
      return '#ffffff';
    }};
  }
`;

const UnivReqFlow = ({ row}) => {
  // User authentication and context
  const { id, designation, designationSuffix, numSuffix, office } = useAuth();
  const user_current_office = useSelector((state) => state?.office?.off || '');
  const user_current_role_array = useSelector((state) => 
    state?.roles?.roles?.[user_current_office] || []
  );


  // State management with validation
  const [requestData, setRequestData] = useState([]);
  const [store_off, setStoreOff] = useState('');
  const [assetDetails, setAssetDetails] = useState({});

  // Check if the request is closed based on multiple possible indicators
  const isRequestClosed = useMemo(() => {
    // Log the row data to debug console (remove in production)
    console.log("Request data:", row);
    
    // Check various possible indicators of a closed status
    return (
      (row?.curr_status?.toLowerCase?.() === 'closed') ||
      // Check for closure tags in the request data
      row?.req_data?.some(item => 
        item.sent_to_tag === 'ureq_paraccept_close' || 
        item.status === 'closed' || 
        item.action?.toLowerCase?.().includes('closed')
      ) ||
      // Check if all partial assets are accepted
      (row?.user_partial_assets?.length > 0 && 
       row?.user_partial_assets?.every(asset => asset.status === 'accepted'))
    );
  }, [row]);

  // Fetch asset details API
  const [fetchFilteredAssets] = useLazyGetAssetsFinalFilteredQuery();

  // Fetch asset details when partial assets are available
  useEffect(() => {
    const allAssetIds = [];
    
    // Get all asset IDs from BO partial issue entries
    if (row?.bo_assets_issue?.length > 0) {
      row.bo_assets_issue.forEach(partialIssue => {
        if (partialIssue.assets && partialIssue.assets.length > 0) {
          allAssetIds.push(...partialIssue.assets);
        }
      });
    }
    
    // Get all asset IDs from store partial issue entries
    if (row?.store_assets_issue?.length > 0) {
      row.store_assets_issue.forEach(storeIssue => {
        if (storeIssue.assets && storeIssue.assets.length > 0) {
          allAssetIds.push(...storeIssue.assets);
        }
      });
    }
    
    if (allAssetIds.length > 0) {
      // Fetch asset details using the filtered API
      fetchFilteredAssets({
        page: 1,
        limit: 999,
        filters: {
          ids: allAssetIds
        }
      }).then(response => {
        if (response?.data?.assets) {
          // Create a map of asset ID to asset details
          const detailsMap = {};
          response.data.assets.forEach(asset => {
            detailsMap[asset._id] = asset;
          });
          setAssetDetails(detailsMap);
        }
      });
    }
  }, [row?.bo_assets_issue, row?.store_assets_issue, fetchFilteredAssets]);

  return (
    <Container>
      {/* Basic Details Section */}
      <BasicDetailsCard>
        <DetailsTitle>Request Details</DetailsTitle>
        <DetailsGrid>
          {/* Debug output - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{fontSize: '11px', color: '#999', gridColumn: '1 / -1'}}>
              curr_status: {row?.curr_status || 'N/A'}, 
              status: {row?.status || 'N/A'}, 
              closed: {isRequestClosed ? 'true' : 'false'}
            </div>
          )}
          
          <DetailItem>
            <DetailLabel>Request ID</DetailLabel>
            <DetailValue>{row?.uniqueReqId}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Request Type</DetailLabel>
            <DetailValue>
              {row?.req_cat === 'internal' ? 'Internal' : 'External'}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              <StatusBadge status={
                isRequestClosed ? 'closed' : 
                row?.req_data?.some(item => item.sent_to_tag === 'boreq_rejct_closed') ? 'rejected' : 'pending'
              }>
                {isRequestClosed ? (
                  'CLOSED'
                ) : row?.req_data?.some(item => ["lvl2req_rejection_appvd", "boreq_rejct_closed"].includes(item.sent_to_tag)) ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CancelOutlinedIcon fontSize="small" /> REJECTED
                  </span>
                ) : 'PENDING'}
              </StatusBadge>
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Created On</DetailLabel>
            <DetailValue>
              {moment(row?.time_stamp).format('DD/MM/YYYY HH:mm')}
            </DetailValue>
          </DetailItem>
          
            <DetailItem>
              <DetailLabel>Requested Store</DetailLabel>
              <DetailValue>{row?.req_store|| 'N/A'}</DetailValue>
            </DetailItem>
          
            <DetailItem>
              <DetailLabel>Requester</DetailLabel>
              <DetailValue>
                {row?.req_creator_details || '-'} 
              </DetailValue>
            </DetailItem>
          
          <DetailItem>
            <DetailLabel>No. of Actions</DetailLabel>
            <DetailValue>{row?.req_data?.length}</DetailValue>
          </DetailItem>
        </DetailsGrid>
        
        {/* Add Requested Assets Table */}
        {row?.assets_requested?.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Requested Assets</DetailLabel>
            {[...row.assets_requested].reverse().map((assetRequest, reqIndex) => (
              <div key={reqIndex} style={{ marginBottom: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {assetRequest.actionNo || '-'}</div>
                  <div>
                    <strong>{reqIndex === row.assets_requested.length - 1 ? "Requested on: " : "Modification Requested on: "}</strong>
                    {moment(assetRequest.time_stamp).format('DD/MM/YYYY HH:mm')}
                  </div>
                </div>
                <AssetTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>No.</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Category</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Asset Name</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetRequest?.data?.map((asset, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{asset?.catName || '-'}</td>
                        <td>{asset?.assetName || '-'}</td>
                        <td>{asset?.quantity || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </AssetTable>
              </div>
            ))}
          </div>
        )}

        {/* Add BO Partial Issued Assets Section */}
        {row?.bo_assets_issue?.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Assets Issued at BO Level</DetailLabel>
            
            {[...row.bo_assets_issue].reverse().map((partialIssue, idx) => (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <AssetSectionHeader status={partialIssue?.status || 'draft'} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {(row.bo_assets_issue.length - idx)}</div>
                  <div>
                    <strong>{idx === row.bo_assets_issue.length - 1 ? "Issued on: " : "Modified on: "}</strong>
                    {moment(partialIssue?.time_stamp).format('DD/MM/YYYY HH:mm')}
                    {partialIssue?.status === 'accepted' && partialIssue?.accept_time && (
                      <span style={{ color: '#2e7d32', marginLeft: '15px' }}>
                        • Accepted on: {moment(partialIssue?.accept_time).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={partialIssue?.status || 'draft'}>
                    {(partialIssue?.status || 'DRAFT').toUpperCase()}
                  </StatusBadge>
                </AssetSectionHeader>
                
                <AssetTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Sl.No.</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>OEM Tag</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>ORG Tag</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Name</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1', width: '45%' }}>Specifications</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partialIssue.assets?.map((assetId, assetIdx) => {
                      const asset = assetDetails[assetId] || {};
                      
                      // Format specifications with flexible layout
                      let formattedSpecs = '-';
                      if (asset?.specification) {
                        const specEntries = Object.entries(asset.specification || {})
                          .filter(([key, value]) => value && value !== '');
                        
                        formattedSpecs = (
                          <div style={{ 
                            fontSize: '12px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px 8px'
                          }}>
                            {specEntries.map(([key, value], index) => (
                              <div key={index} style={{ 
                                minWidth: '120px', 
                                maxWidth: '180px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                <strong>{key}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      
                      return (
                        <AssetRow key={assetIdx} status={partialIssue?.status || 'draft'}>
                          <td>{assetIdx + 1}</td>
                          <td>{asset?.oem_tag || '-'}</td>
                          <td>{asset?.org_tag || '-'}</td>
                          <td>{asset?.name || '-'}</td>
                          <td>{formattedSpecs}</td>
                        </AssetRow>
                      );
                    })}
                  </tbody>
                </AssetTable>
                
                {/* Add comparison section for modified assets (when idx > 0) */}
                {idx < row.bo_assets_issue.length - 1 && (
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f5f5f5', 
                    fontSize: '13px',
                    borderRadius: '0 0 4px 4px',
                    marginBottom: '5px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Changes from Previous Version:</div>
                    {(() => {
                      const currAssets = partialIssue.assets || [];
                      const prevAssets = row.bo_assets_issue[row.bo_assets_issue.length - idx - 2]?.assets || [];
                      const added = currAssets.filter(id => !prevAssets.includes(id)).length;
                      const removed = prevAssets.filter(id => !currAssets.includes(id)).length;
                      
                      return (
                        <div style={{ display: 'flex', gap: '15px' }}>
                          {added > 0 && (
                            <div style={{ color: '#2e7d32' }}>
                              <strong>+{added}</strong> asset{added !== 1 ? 's' : ''} added
                            </div>
                          )}
                          {removed > 0 && (
                            <div style={{ color: '#d32f2f' }}>
                              <strong>-{removed}</strong> asset{removed !== 1 ? 's' : ''} removed
                            </div>
                          )}
                          {added === 0 && removed === 0 && (
                            <div style={{ color: '#757575' }}>
                              No changes in assets, other details may have been updated
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add Store Partial Issued Assets Section */}
        {row?.store_assets_issue?.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Assets Issued by Store</DetailLabel>
            
            {[...row.store_assets_issue].reverse().map((storeIssue, idx) => (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <AssetSectionHeader status={storeIssue?.status || 'draft'} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {(row.store_assets_issue.length - idx)}</div>
                  <div>
                    <strong>{idx === row.store_assets_issue.length - 1 ? "Issued on: " : "Modified on: "}</strong>
                    {moment(storeIssue?.time_stamp).format('DD/MM/YYYY HH:mm')}
                    {storeIssue?.status === 'accepted' && storeIssue?.accept_time && (
                      <span style={{ color: '#2e7d32', marginLeft: '15px' }}>
                        • Accepted on: {moment(storeIssue?.accept_time).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={storeIssue?.status || 'draft'}>
                    {(storeIssue?.status || 'DRAFT').toUpperCase()}
                  </StatusBadge>
                </AssetSectionHeader>
                
                <AssetTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: storeIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Sl.No.</th>
                      <th style={{ backgroundColor: storeIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>OEM Tag</th>
                      <th style={{ backgroundColor: storeIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>ORG Tag</th>
                      <th style={{ backgroundColor: storeIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Name</th>
                      <th style={{ backgroundColor: storeIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1', width: '45%' }}>Specifications</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeIssue.assets?.map((assetId, assetIdx) => {
                      const asset = assetDetails[assetId] || {};
                      
                      // Format specifications with flexible layout
                      let formattedSpecs = '-';
                      if (asset?.specification) {
                        const specEntries = Object.entries(asset.specification || {})
                          .filter(([key, value]) => value && value !== '');
                        
                        formattedSpecs = (
                          <div style={{ 
                            fontSize: '12px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px 8px'
                          }}>
                            {specEntries.map(([key, value], index) => (
                              <div key={index} style={{ 
                                minWidth: '120px', 
                                maxWidth: '180px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                <strong>{key}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      
                      return (
                        <AssetRow key={assetIdx} status={storeIssue?.status || 'draft'}>
                          <td>{assetIdx + 1}</td>
                          <td>{asset?.oem_tag || '-'}</td>
                          <td>{asset?.org_tag || '-'}</td>
                          <td>{asset?.name || '-'}</td>
                          <td>{formattedSpecs}</td>
                        </AssetRow>
                      );
                    })}
                  </tbody>
                </AssetTable>
                
                {/* Add comparison section for modified assets (when idx > 0) */}
                {idx < row.store_assets_issue.length - 1 && (
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f5f5f5', 
                    fontSize: '13px',
                    borderRadius: '0 0 4px 4px',
                    marginBottom: '5px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Changes from Previous Version:</div>
                    {(() => {
                      const currAssets = storeIssue.assets || [];
                      const prevAssets = row.store_assets_issue[row.store_assets_issue.length - idx - 2]?.assets || [];
                      const added = currAssets.filter(id => !prevAssets.includes(id)).length;
                      const removed = prevAssets.filter(id => !currAssets.includes(id)).length;
                      
                      return (
                        <div style={{ display: 'flex', gap: '15px' }}>
                          {added > 0 && (
                            <div style={{ color: '#2e7d32' }}>
                              <strong>+{added}</strong> asset{added !== 1 ? 's' : ''} added
                            </div>
                          )}
                          {removed > 0 && (
                            <div style={{ color: '#d32f2f' }}>
                              <strong>-{removed}</strong> asset{removed !== 1 ? 's' : ''} removed
                            </div>
                          )}
                          {added === 0 && removed === 0 && (
                            <div style={{ color: '#757575' }}>
                              No changes in assets, other details may have been updated
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add User Partial Issued Assets Section */}
        {row?.user_partial_assets?.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>User Partial Issued Assets</DetailLabel>
            
            {[...row.user_partial_assets].reverse().map((partialIssue, idx) => (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <AssetSectionHeader status={partialIssue?.status || 'pending'} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {(row.user_partial_assets.length - idx)}</div>
                  <div>
                    <strong>{idx === row.user_partial_assets.length - 1 ? "Issued on: " : "Modified on: "}</strong>
                    {moment(partialIssue?.time_stamp).format('DD/MM/YYYY HH:mm')}
                    {partialIssue?.status === 'accepted' && partialIssue?.accept_time && (
                      <span style={{ color: '#2e7d32', marginLeft: '15px' }}>
                        • Accepted on: {moment(partialIssue?.accept_time).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={partialIssue?.status || 'pending'}>
                    {(partialIssue?.status || 'PENDING').toUpperCase()}
                  </StatusBadge>
                </AssetSectionHeader>
                
                <AssetTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Sl.No.</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>OEM Tag</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>ORG Tag</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Name</th>
                      <th style={{ backgroundColor: partialIssue?.status === 'accepted' ? '#e8f5e9' : '#fff8f1', width: '45%' }}>Specifications</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partialIssue.assets.map((assetId, assetIdx) => {
                      const asset = assetDetails[assetId] || {};
                      
                      // Format specifications with flexible layout
                      let formattedSpecs = '-';
                      if (asset?.specification) {
                        const specEntries = Object.entries(asset.specification || {})
                          .filter(([key, value]) => value && value !== '');
                        
                        formattedSpecs = (
                          <div style={{ 
                            fontSize: '12px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px 8px'
                          }}>
                            {specEntries.map(([key, value], index) => (
                              <div key={index} style={{ 
                                minWidth: '120px', 
                                maxWidth: '180px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                <strong>{key}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      
                      return (
                        <AssetRow key={assetIdx} status={partialIssue?.status || 'pending'}>
                          <td>{assetIdx + 1}</td>
                          <td>{asset?.oem_tag || '-'}</td>
                          <td>{asset?.org_tag || '-'}</td>
                          <td>{asset?.name || '-'}</td>
                          <td>{formattedSpecs}</td>
                        </AssetRow>
                      );
                    })}
                  </tbody>
                </AssetTable>
                
                {/* Add comparison section for modified assets (when idx > 0) */}
                {idx < row.user_partial_assets.length - 1 && (
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f5f5f5', 
                    fontSize: '13px',
                    borderRadius: '0 0 4px 4px',
                    marginBottom: '5px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Changes from Previous Version:</div>
                    {(() => {
                      const currAssets = partialIssue.assets || [];
                      const prevAssets = row.user_partial_assets[row.user_partial_assets.length - idx - 2]?.assets || [];
                      const added = currAssets.filter(id => !prevAssets.includes(id)).length;
                      const removed = prevAssets.filter(id => !currAssets.includes(id)).length;
                      
                      return (
                        <div style={{ display: 'flex', gap: '15px' }}>
                          {added > 0 && (
                            <div style={{ color: '#2e7d32' }}>
                              <strong>+{added}</strong> asset{added !== 1 ? 's' : ''} added
                            </div>
                          )}
                          {removed > 0 && (
                            <div style={{ color: '#d32f2f' }}>
                              <strong>-{removed}</strong> asset{removed !== 1 ? 's' : ''} removed
                            </div>
                          )}
                          {added === 0 && removed === 0 && (
                            <div style={{ color: '#757575' }}>
                              No changes in assets, other details may have been updated
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </BasicDetailsCard>

      <DetailsTitle>Request Timeline</DetailsTitle>
      <TimelineContainer>
        {row?.req_data?.map((entry, index) => (
          <TimelineItemContainer key={index}>
            <TimelineCard 
              isEven={index % 2 === 1} 
              status={
                entry?.sent_to_tag === 'boreq_rejct_closed' ||
                entry?.sent_to_tag === "lvl2req_rejection_appvd" ? 'rejected' : 
                entry?.sent_to_tag === 'ureq_paraccept_close' || 
                entry?.sent_to_tag === 'ureq_allasstbolvl_close' || 
                entry?.sent_to_tag === 'ureq_storeassetsaccpt_close' ? 'accepted' : 
                entry?.curr_status?.toLowerCase() === 'closed' ? 'accepted' :
                'pending'
              }
            >
              <TimelineHeader>
                <TimelineTitle>Action {index + 1}</TimelineTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <TimelineTime>
                    {moment(entry?.time_stamp).format('DD/MM/YYYY HH:mm')}
                  </TimelineTime>
                  <StatusBadge 
                    status={
                      entry?.sent_to_tag === 'boreq_rejct_closed' ||
                      entry?.sent_to_tag === "lvl2req_rejection_appvd" ? 'rejected' : 
                      entry?.sent_to_tag === 'ureq_paraccept_close' || 
                      entry?.sent_to_tag === 'ureq_allasstbolvl_close' || 
                      entry?.sent_to_tag === 'ureq_storeassetsaccpt_close' ? 'accepted' : 
                      entry?.curr_status?.toLowerCase() === 'closed' ? 'accepted' :
                      'pending'
                    }
                  >
                    {entry?.sent_to_tag === 'boreq_rejct_closed' ||
                     entry?.sent_to_tag === "lvl2req_rejection_appvd" ? 'REJECTED' : 
                     entry?.sent_to_tag === 'ureq_paraccept_close' || 
                     entry?.sent_to_tag === 'ureq_allasstbolvl_close' || 
                     entry?.sent_to_tag === 'ureq_storeassetsaccpt_close' || 
                     entry?.curr_status?.toLowerCase() === 'closed' ? 'ACCEPTED/CLOSED' :
                     'PENDING'}
                  </StatusBadge>
                </div>
              </TimelineHeader>

              {entry?.action && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '10px 12px', 
                  background: entry?.sent_to_tag === 'boreq_rejct_closed' ||
                              entry?.sent_to_tag === "lvl2req_rejection_appvd" ? '#ffebee' : 
                             entry?.sent_to_tag === 'ureq_paraccept_close' || 
                             entry?.sent_to_tag === 'ureq_allasstbolvl_close' || 
                             entry?.sent_to_tag === 'ureq_storeassetsaccpt_close' || 
                             entry?.curr_status?.toLowerCase() === 'closed' ? '#e8f5e9' : 
                             '#f0f8ff', 
                  borderRadius: '4px',
                  borderLeft: `3px solid ${
                    entry?.sent_to_tag === 'boreq_rejct_closed' ||
                    entry?.sent_to_tag === "lvl2req_rejection_appvd" ? '#d32f2f' : 
                    entry?.sent_to_tag === 'ureq_paraccept_close' || 
                    entry?.sent_to_tag === 'ureq_allasstbolvl_close' || 
                    entry?.sent_to_tag === 'ureq_storeassetsaccpt_close' || 
                    entry?.curr_status?.toLowerCase() === 'closed' ? '#4caf50' : 
                    '#1976d2'
                  }`,
                  fontSize: '16px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {entry.action}
                </div>
              )}

              {entry?.assets_details && entry.assets_details.length > 0 && (
                <AssetDetails>
                  <h4>Requested Assets</h4>
                  <AssetTable>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Asset Name</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.assets_details.map((asset, idx) => (
                        <AssetRow key={idx} status={asset?.status || 'pending'}>
                          <td>{asset?.catName || '-'}</td>
                          <td>{asset?.assetName || '-'}</td>
                          <td>{asset?.quantity || '-'}</td>
                        </AssetRow>
                      ))}
                    </tbody>
                  </AssetTable>
                </AssetDetails>
              )}

              <Remark>
                <h4>Remark</h4>
                <div dangerouslySetInnerHTML={{ __html: entry?.Text || '' }} />
              </Remark>

              <UserInfo>
                <div style={{ marginBottom: '5px' }}>
                  <strong>From:</strong> {entry?.sent_by?.name?.firstName || '-'} - {entry?.sent_by?.office || '-'} - {entry?.sent_by?.designation || '-'}
                </div>
                <div>
                  <strong>To:</strong> {entry?.sent_to?.name?.firstName || '-'} - {entry?.sent_to?.office || '-'} - {entry?.sent_to?.designation || '-'}
                </div>
              </UserInfo>
            </TimelineCard>
          </TimelineItemContainer>
        ))}
      </TimelineContainer>
    </Container>
  );
};

export default UnivReqFlow;

